// The key hierarchy of PROTOCOL.md §2, on WebCrypto (Workers, browsers, Node 22). Every function takes and returns
// raw bytes so test vectors pin them; browsers wrap the AVK in a non-extractable key (`importAVK`) for storage.
import { concat, fromCrockford, toCrockford, toB64u, toHex, utf8 } from './b64.ts';

type Bytes = Uint8Array<ArrayBuffer>;
const subtle = crypto.subtle;

export const random = (n: number): Bytes => crypto.getRandomValues(new Uint8Array(n));

export async function sha256(data: Uint8Array): Promise<Bytes> {
  return new Uint8Array(await subtle.digest('SHA-256', data as Bytes));
}

/** HKDF-SHA-256, empty salt unless given, 32 bytes. */
export async function hkdf(ikm: Uint8Array, info: string, salt: Uint8Array = new Uint8Array(0)): Promise<Bytes> {
  const key = await subtle.importKey('raw', ikm as Bytes, 'HKDF', false, ['deriveBits']);
  const bits = await subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: salt as Bytes, info: utf8(info) }, key, 256);
  return new Uint8Array(bits);
}

/** AES-256-GCM: nonce ‖ ciphertext ‖ tag. `nonce` only for test vectors. */
export async function seal(key: Uint8Array, plaintext: Uint8Array, aad?: string, nonce: Uint8Array = random(12)): Promise<Bytes> {
  const k = await subtle.importKey('raw', key as Bytes, 'AES-GCM', false, ['encrypt']);
  const params = { name: 'AES-GCM', iv: nonce as Bytes, ...(aad === undefined ? {} : { additionalData: utf8(aad) }) };
  return concat(nonce, new Uint8Array(await subtle.encrypt(params, k, plaintext as Bytes)));
}

export async function open(key: Uint8Array, sealed: Uint8Array, aad?: string): Promise<Bytes> {
  if (sealed.length < 28) throw new Error('sealed value too short');
  const k = await subtle.importKey('raw', key as Bytes, 'AES-GCM', false, ['decrypt']);
  const params = { name: 'AES-GCM', iv: sealed.slice(0, 12), ...(aad === undefined ? {} : { additionalData: utf8(aad) }) };
  return new Uint8Array(await subtle.decrypt(params, k, sealed.slice(12)));
}

/** First 8 bytes of SHA-256, hex (`RecordCodec.fingerprint`). */
export async function fingerprint(key: Uint8Array): Promise<string> {
  return toHex((await sha256(key)).subarray(0, 8));
}

// Derivations (§2.2) ---------------------------------------------------------------------------------------------

export const prfSalt = (): Promise<Bytes> => sha256(utf8('writing-suite/prf/v1'));
export const passkeyKEK = (prfOutput: Uint8Array) => hkdf(prfOutput, 'writing-suite/passkey-kek/v1');
export const recoveryKEK = (secret: Uint8Array) => hkdf(secret, 'writing-suite/recovery-kek/v1');
export const projectWrapKey = (avk: Uint8Array) => hkdf(avk, 'writing-suite/project-wrap/v1');
export const accountDocKey = (avk: Uint8Array) => hkdf(avk, 'writing-suite/account-doc/v1');
export const attachmentNameKey = (projectKey: Uint8Array) => hkdf(projectKey, 'writing-suite/attachment-name/v1');

export async function recoveryVerifier(secret: Uint8Array): Promise<string> {
  return toB64u(await sha256(await hkdf(secret, 'writing-suite/recovery-verifier/v1')));
}

// Wrapped values (§2.3) ------------------------------------------------------------------------------------------

export const vaultAAD = (accountId: string, credentialId: string | 'recovery') => `writing-suite/vault/v1:${accountId}:${credentialId}`;
export const projectKeyAAD = (accountId: string, projectId: string) => `writing-suite/project-key/v1:${accountId}:${projectId}`;

export async function wrapProjectKey(avk: Uint8Array, accountId: string, projectId: string, projectKey: Uint8Array, nonce?: Uint8Array) {
  return seal(await projectWrapKey(avk), projectKey, projectKeyAAD(accountId, projectId), nonce);
}

export async function unwrapProjectKey(avk: Uint8Array, accountId: string, projectId: string, wrapped: Uint8Array) {
  return open(await projectWrapKey(avk), wrapped, projectKeyAAD(accountId, projectId));
}

export interface ProjectSummary {
  v: 1;
  title: string;
  type?: string;
  series?: string;
  words?: number;
  modifiedAt?: string;
}

export async function sealSummary(projectKey: Uint8Array, summary: ProjectSummary, nonce?: Uint8Array) {
  return seal(projectKey, utf8(JSON.stringify(summary)), undefined, nonce);
}

export async function openSummary(projectKey: Uint8Array, sealed: Uint8Array): Promise<ProjectSummary> {
  return JSON.parse(new TextDecoder().decode(await open(projectKey, sealed)));
}

export async function attachmentDoc(projectKey: Uint8Array, sha: Uint8Array): Promise<string> {
  const key = await subtle.importKey('raw', await attachmentNameKey(projectKey), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return `attachment.${toHex(new Uint8Array(await subtle.sign('HMAC', key, sha as Bytes)))}`;
}

// Recovery key (§2.4) --------------------------------------------------------------------------------------------

export async function formatRecoveryKey(secret: Uint8Array): Promise<string> {
  if (secret.length !== 32) throw new Error('recovery secret is 32 bytes');
  const text = toCrockford(concat(secret, (await sha256(secret)).subarray(0, 3)));
  return `WS1-${text.match(/.{4}/g)!.join('-')}`;
}

export async function parseRecoveryKey(text: string): Promise<Bytes> {
  const body = text.trim().replace(/^WS[1IL][-\s]*/i, ''); // the prefix's 1 is as easy to mistype as the rest
  const bytes = fromCrockford(body, 35);
  const secret = bytes.slice(0, 32);
  const check = (await sha256(secret)).subarray(0, 3);
  if (check.some((b, i) => b !== bytes[32 + i])) throw new Error('That recovery key has a typo: its check digits don’t match.');
  return secret;
}

// Pairing (§2.5) -------------------------------------------------------------------------------------------------

export async function pairingCode(requesterPub: Uint8Array, nonce: Uint8Array): Promise<string> {
  const code = toCrockford(await sha256(concat(utf8('writing-suite/pairing-code/v1'), requesterPub, nonce)), 50);
  return `${code.slice(0, 5)}-${code.slice(5)}`;
}

export const sameCode = (a: string, b: string) => a.toUpperCase().replace(/[-\s]/g, '') === b.toUpperCase().replace(/[-\s]/g, '');

export interface EphemeralKey {
  pub: Bytes;
  privateKey: CryptoKey;
}

export async function ephemeralKey(): Promise<EphemeralKey> {
  const pair = (await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits'])) as CryptoKeyPair;
  return { pub: new Uint8Array((await subtle.exportKey('raw', pair.publicKey)) as ArrayBuffer), privateKey: pair.privateKey };
}

/** Imports a P-256 private key from PKCS#8 (test vectors only). */
export async function importEphemeral(pkcs8: Uint8Array, pub: Uint8Array): Promise<EphemeralKey> {
  const privateKey = await subtle.importKey('pkcs8', pkcs8 as Bytes, { name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
  return { pub: pub as Bytes, privateKey };
}

export async function pairingKey(own: EphemeralKey, otherPub: Uint8Array, requesterPub: Uint8Array, approverPub: Uint8Array): Promise<Bytes> {
  const other = await subtle.importKey('raw', otherPub as Bytes, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
  // The standard field is `public` (Workers' type definitions call it `$public`; the runtime takes `public`).
  const algorithm = { name: 'ECDH', public: other } as unknown as Parameters<typeof subtle.deriveBits>[0];
  const shared = new Uint8Array(await subtle.deriveBits(algorithm, own.privateKey, 256));
  return hkdf(shared, 'writing-suite/pairing/v1', concat(requesterPub, approverPub));
}

export const pairingAAD = (pairingId: string) => `writing-suite/pairing/v1:${pairingId}`;
